/**
 * mescreen immersive enhancements.
 *
 *   1. Scroll-triggered reveal: <section>s inside <main> fade/slide in as
 *      they enter the viewport. Auto-applied, opt-out with [data-no-reveal].
 *
 *   2. Proactive agent prompts: when a section tagged with
 *      [data-mes-trigger="symptoms|science|pricing"] dwells in the viewport
 *      past a threshold, the chat widget opens with a contextual greeting.
 *      The script also auto-tags sections whose headings match known
 *      keyword patterns, so it works out-of-box without theme edits.
 *
 * Idempotent and safe to load multiple times. Respects prefers-reduced-motion.
 */
(function () {
  "use strict";
  if (window.__mescreenImmersiveLoaded) return;
  window.__mescreenImmersiveLoaded = true;

  var prefersReducedMotion =
    window.matchMedia &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  // ------------------------------------------------------------------ reveal
  if (!prefersReducedMotion && "IntersectionObserver" in window) {
    var revealObserver = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            entry.target.classList.add("mes-revealed");
            revealObserver.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.12, rootMargin: "0px 0px -80px 0px" }
    );

    function applyReveal() {
      // Only target content sections — avoid the widget root, sticky CTA,
      // Klaviyo popup containers, header/footer, and anything explicitly
      // opted-out.
      var candidates = document.querySelectorAll(
        "main section, main > div > section, [data-mes-reveal]"
      );
      candidates.forEach(function (el) {
        if (el.hasAttribute("data-no-reveal")) return;
        if (el.closest("[data-no-reveal]")) return;
        if (
          el.closest(
            "#mescreen-agent-root, .mescreen-sticky-cta, .mescreen-quiz-cta-wrap, header, footer, nav"
          )
        )
          return;
        if (el.classList.contains("mes-reveal")) return;
        el.classList.add("mes-reveal");
        revealObserver.observe(el);
      });
    }

    if (document.readyState === "loading") {
      document.addEventListener("DOMContentLoaded", applyReveal);
    } else {
      applyReveal();
    }
  }

  // ---------------------------------------------------------------- triggers
  // Each trigger = one proactive greeting. `dwellMs` is how long the section
  // must stay in view before we fire, so we don't ambush fast scrollers.
  var TRIGGERS = {
    symptoms: {
      dwellMs: 4500,
      greeting:
        "Reading about fatigue and brain fog? Those are real signals — not just 'getting older.' Want me to help figure out if mescreen could explain what you're feeling?",
      suggestions: [
        "I feel most of these",
        "What does the test actually show?",
        { label: "📩 Send me more info", primary: true },
      ],
    },
    science: {
      dwellMs: 4000,
      greeting:
        "The NASA-origin bit is real — same methodology, just adapted for at-home use. Want me to break down what the test actually measures, in plain English?",
      suggestions: [
        "How does the test work?",
        "How long until results?",
        { label: "📩 Send me more info", primary: true },
      ],
    },
    pricing: {
      dwellMs: 3500,
      greeting:
        "Thinking about it? Happy to answer anything before you decide — what's included, how long it takes, whether there's a discount.",
      suggestions: [
        "What's included for $699?",
        "Is there a discount?",
        "How long until results?",
      ],
    },
  };

  // Keyword patterns used to auto-tag matching sections. Runs once on load.
  // If the theme already has explicit data-mes-trigger attributes, those win.
  var AUTO_PATTERNS = {
    symptoms: /(brain\s*fog|fatigue|tired|exhaust|memory\s*loss|burn\s*out|depression)/i,
    science: /(nasa|twin\s*study|how\s*it\s*works|the\s*science|mitochondrial\s*function)/i,
    pricing: /(\$\s*699|order\s*your\s*test|add\s*to\s*cart|get\s*started|buy\s*now)/i,
  };

  function autoTagFromHeadings() {
    var headings = document.querySelectorAll("h1, h2, h3");
    headings.forEach(function (h) {
      var text = (h.textContent || "").trim();
      if (!text) return;
      Object.keys(AUTO_PATTERNS).forEach(function (name) {
        if (!AUTO_PATTERNS[name].test(text)) return;
        var host = h.closest("section") || h.closest("[data-section-type]") || h.parentElement;
        if (!host || host.hasAttribute("data-mes-trigger")) return;
        host.setAttribute("data-mes-trigger", name);
      });
    });
  }

  // Fire at most once per trigger per session, so we're not obnoxious.
  var firedKey = "mescreen_triggers_fired";
  var fired;
  try {
    fired = new Set(JSON.parse(sessionStorage.getItem(firedKey) || "[]"));
  } catch (_) {
    fired = new Set();
  }
  function markFired(name) {
    fired.add(name);
    try {
      sessionStorage.setItem(firedKey, JSON.stringify(Array.from(fired)));
    } catch (_) {}
  }

  var dwellTimers = new Map();

  function tryFire(name) {
    if (fired.has(name)) return;
    // Don't proactively open if the user has already opened/used the chat.
    if (window.mescreen && window.mescreen.isOpen && window.mescreen.isOpen()) return;
    if (!window.mescreen || typeof window.mescreen.open !== "function") return;
    var t = TRIGGERS[name];
    if (!t) return;
    markFired(name);
    window.mescreen.open({ greeting: t.greeting, suggestions: t.suggestions });
  }

  function initTriggers() {
    if (!("IntersectionObserver" in window)) return;
    autoTagFromHeadings();

    var triggerObserver = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          var name = entry.target.getAttribute("data-mes-trigger");
          if (!name || !TRIGGERS[name]) return;
          if (entry.isIntersecting && entry.intersectionRatio >= 0.3) {
            if (!dwellTimers.has(entry.target)) {
              var timer = setTimeout(function () {
                tryFire(name);
              }, TRIGGERS[name].dwellMs);
              dwellTimers.set(entry.target, timer);
            }
          } else {
            var existing = dwellTimers.get(entry.target);
            if (existing) {
              clearTimeout(existing);
              dwellTimers.delete(entry.target);
            }
          }
        });
      },
      { threshold: [0, 0.3, 0.5] }
    );

    document.querySelectorAll("[data-mes-trigger]").forEach(function (el) {
      triggerObserver.observe(el);
    });
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", initTriggers);
  } else {
    initTriggers();
  }
})();
